import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { cn } from '../../lib/cn'
import { useTelefonLayout } from '../../lib/medien'
import { termineHeuteQuery } from '../../lib/queries'

/**
 * **Die heutigen Termine – eine Zeile über dem Tag** ([Z-17](../../../../../docs/phasen/zusatz-17-outlook.md)).
 *
 * Nils: *„dass man seinen Kalender und seine ToDos kriegt."* Nur lesen, nur heute: „Mein Tag"
 * beantwortet, was ich heute tue – ein Termin ist ein Stück davon, das nicht aus Work kommt.
 * Mehr als heute wäre ein Kalender, und den gibt es schon.
 *
 * Nichts zu zeigen ⇒ nichts da: ohne Verbindung, ohne Termine und bei einem Fehler, der keinen
 * Termin betrifft, steht hier keine leere Karte. Der laufende Termin ist hervorgehoben,
 * vergangene sind blass.
 */
export function TermineHeute() {
  const { data } = useQuery(termineHeuteQuery)
  const telefon = useTelefonLayout()
  const [vergangeneZeigen, setVergangeneZeigen] = useState(false)
  if (!data?.verbunden || data.termine.length === 0) return null

  const jetzt = Date.now()
  const istVorbei = (t: { ende: string }) => {
    const ende = zeit(t.ende)
    return ende !== null && ende < jetzt
  }
  /**
   * **Auf dem Telefon nur, was noch kommt** (06.10.2026). Nachmittags standen dort zehn
   * durchgestrichene Termine untereinander, und „Mein Tag" begann einen halben Bildschirm tiefer.
   * Vorbei ist vorbei – ein Tipp auf „+ 10 vorbei" holt sie zurück. Am Rechner bleibt alles in
   * einer Zeile, dort kostet es keinen Platz.
   */
  const vorbeiAnzahl = data.termine.filter(istVorbei).length
  const verbergen = telefon && !vergangeneZeigen
  const gezeigt = verbergen ? data.termine.filter((t) => !istVorbei(t)) : data.termine

  return (
    <div className="mb-3.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px]">
      <span className="font-semibold text-ink2">Heute</span>
      {telefon && vorbeiAnzahl > 0 && (
        <button
          type="button"
          onClick={() => setVergangeneZeigen((x) => !x)}
          aria-expanded={vergangeneZeigen}
          className="tap cursor-pointer rounded-md border border-line px-2 py-[2px] text-muted"
        >
          {vergangeneZeigen ? 'vergangene ausblenden' : `+ ${vorbeiAnzahl} vorbei`}
        </button>
      )}
      {telefon && gezeigt.length === 0 && <span className="text-muted">nichts mehr heute</span>}
      {gezeigt.map((t) => {
        const beginn = zeit(t.beginn)
        const ende = zeit(t.ende)
        const vorbei = ende !== null && ende < jetzt
        const laeuft = beginn !== null && ende !== null && beginn <= jetzt && jetzt < ende
        return (
          <a
            key={t.id}
            href={t.webLink || undefined}
            target="_blank"
            rel="noreferrer"
            title={[t.betreff, t.ort].filter(Boolean).join(' · ')}
            className={cn(
              'inline-flex max-w-full items-center gap-1 rounded-md border px-2 py-[2px]',
              laeuft
                ? 'border-accent bg-accent-soft font-semibold text-accent-deep'
                : vorbei
                  ? 'border-line text-muted line-through decoration-line2'
                  : 'border-line bg-surface text-ink2',
              t.zeigtAls === 'tentative' && 'border-dashed',
            )}
          >
            <span className="tabular shrink-0">
              {t.ganztaegig ? 'ganztägig' : `${uhr(t.beginn)}–${uhr(t.ende)}`}
            </span>
            <span className="truncate">{t.betreff}</span>
          </a>
        )
      })}
    </div>
  )
}

/** Graph liefert die Zeit in der angefragten Zeitzone ohne Versatz – hier als Ortszeit lesen. */
function zeit(wert: string): number | null {
  if (!wert) return null
  const d = new Date(wert.replace(/\.\d+$/, ''))
  return Number.isNaN(d.getTime()) ? null : d.getTime()
}

function uhr(wert: string): string {
  const t = zeit(wert)
  return t === null
    ? ''
    : new Date(t).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
}
